import React, { useContext, useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { DealershipLoader } from "../../components/common/dealershipLoader/dealershipLoader"

import { Dropdown } from "../../components/common/dropdown"
import { Input } from "../../components/common/input/Input"
import { AppContext, AppContextInterface } from "../../state/ContextProvider"
import {
  getDealershipUrl,
  getGlobalDealershipId,
} from "../../utils/dealership-utils"
import { GeoLocation } from "../../utils/geolocation"
import { GroupIds } from "../../utils/group-utils"
import { UseFormMethods } from "react-hook-form"

interface Props extends Partial<Pick<UseFormMethods, "register" | "errors">> {
  postCode?: string
  groupId?: number
  errorForNoDealershipGuid: (props: any) => void
  setHasPostCodeError?: (props: any) => void
  hasPostCodeError?: boolean
}

const DealershipContent: React.FC<Props> = ({
  errorForNoDealershipGuid,
  register: parentRegister,
  errors: parentErrors,
  setHasPostCodeError,
}) => {
  const {
    actions: {
      addContactDetails,
      addAppointmentDetails,
      addDealershipGuid,
      addClosestDealerships,
      addDealershipUrl,
    },
    state: {
      contactDetails,
      appointmentDetails,
      closestDealerships,
      appointmentDetails: { postCode, dealership },
      groupId,
    },
  } = useContext(AppContext) as AppContextInterface

  const { register, errors } = useForm<FormData>({
    mode: "all",
  })

  const errorMessage = {
    dealership:
      "Oops something has gone wrong, please try again or select another dealership",
    postcode: "Please enter a valid postcode",
  }

  const [postCodeError, setPostCodeError] = useState(false)
  const [dealershipError, setDealershipError] = useState(false)
  const [isButtonClicked, setButtonClicked] = useState(false)
  const [dealershipCtaDisabled, setDealershipCtaDisabled] = useState(true)
  const [validPostcode, setValidPostcode] = useState(true)
  const [validPostCodeError, setValidPostCodeError] = useState(false)

  useEffect(() => {
    const findDealershipGuid = () => {
      if (dealership && closestDealerships.length > 0) {
        const dealershipUrl = getDealershipUrl(closestDealerships, dealership)
        const globalDealershipId = getGlobalDealershipId(
          closestDealerships,
          dealership
        )
        if (globalDealershipId === null) {
          setDealershipError(true)
          errorForNoDealershipGuid(true)
        } else {
          addDealershipUrl(dealershipUrl)
          errorForNoDealershipGuid(false)
          addDealershipGuid(globalDealershipId)
          setDealershipError(false)
        }
      }
    }
    findDealershipGuid()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closestDealerships, dealership])

  useEffect(() => {
    if (postCodeError && validPostcode) {
      setValidPostCodeError(true)
    }
  }, [postCodeError, validPostcode])

  useEffect(() => {
    if (closestDealerships && closestDealerships.length === 1) {
      if (dealership !== closestDealerships[0].Value) {
        addAppointmentDetails({
          ...appointmentDetails,
          dealership: closestDealerships[0].Value,
        })
      }
    }
  }, [
    closestDealerships,
    appointmentDetails,
    addAppointmentDetails,
    dealership,
  ])

  useEffect(() => {
    if(dealershipCtaDisabled) {
      setHasPostCodeError && setHasPostCodeError(true)
    } else {
      setHasPostCodeError && setHasPostCodeError(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealershipCtaDisabled])

  return (
    <>
      <p className="input-text">Postcode *</p>
      <Input
        className="appointment-input"
        type="text"
        defaultValue={postCode}
        name="postCode"
        register={register}
        placeholder="Enter your postcode"
        rules={{
          required: "Please enter your postcode",
          pattern: {
            value:
              // eslint-disable-next-line no-useless-escape
              /^([Gg][Ii][Rr] 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([AZa-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9]?[A-Za-z]))))[\s]*[0-9][A-Za-z]{2})$/,
            message: errorMessage.postcode,
          },
        }}
        onChange={(e) => {
          addAppointmentDetails({
            ...appointmentDetails,
            postCode: e.target.value,
          })
          addContactDetails({
            ...contactDetails,
            postcode: e.target.value,
          })
          if (Object.keys(errors).length === 0) {
            setValidPostcode(true)
            setPostCodeError(false)
          } else {
            setValidPostcode(false)
            setPostCodeError(true)
          }
          setValidPostCodeError(false)
        }}
        errors={errors}
      />
      {postCodeError && Object.keys(errors).length === 0 && !validPostcode ? (
        <div className="post-code-error">
          <p>Please enter a valid postcode</p>
        </div>
      ) : null}
      {validPostCodeError ? (
        <div className="post-code-error">
          <p>Something went wrong. Please check your postcode for errors and try again, or try a different postcode.</p>
        </div>
      ) : null}
      <div className="button-container">
        <GeoLocation
          postCode={postCode}
          setButtonClicked={setButtonClicked}
          setPostCodeError={setPostCodeError}
          groupId={GroupIds[groupId]}
          addClosestDealerships={addClosestDealerships}
          closestDealerships={closestDealerships}
          dealershipCtaDisabled={dealershipCtaDisabled}
          setDealershipCtaDisabled={setDealershipCtaDisabled}
          rendersFindDealershipCta={true}
          validPostcode={validPostcode}
        />
      </div>
      {isButtonClicked && !closestDealerships.length && postCode ? (
        <div className="dealership-loading-spinner">
          <DealershipLoader />
        </div>
      ) : null}
      {closestDealerships && closestDealerships.length ? (
        <Dropdown
          label=""
          defaultValue={dealership}
          options={closestDealerships}
          disabled={
            !postCodeError && closestDealerships?.length > 0 ? false : true
          }
          placeholder="Select your dealership"
          ref={
            parentRegister &&
            parentRegister({ required: "Please select a dealership" })
          }
          name="select-dealership"
          errors={parentErrors}
          onChange={(e: any) => {
            const { target } = e
            if (target && target.value) {
              addAppointmentDetails({
                ...appointmentDetails,
                dealership: e.target.value,
              })
            }
          }}
        />
      ) : null}
      {dealershipError ? (
        <div className="post-code-error">
          <p>{errorMessage.dealership}</p>
        </div>
      ) : null}
    </>
  )
}

export default DealershipContent
